import {
  Box,
  Button,
  type ButtonProps,
  Center,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Fade,
  Flex,
  Heading,
  useDisclosure,
  useMediaQuery,
} from '@chakra-ui/react'
import { faBars, faTimes } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { usePathname } from 'next/navigation'
import React, { useEffect, useMemo, useState } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import getActiveNavbarLink from '@/utils/getActiveNavbarLink'

import { useUser } from '@/client/context/user'
import { trackEvent } from '@/client/services/analytics'
import { faq, getDashboardUrl, getDonateUrl } from '@/client/services/url'
import { isUserOnboarding } from '@/client/services/user'

import SignUpLink from '@/components/Auth/SignUpLink'
import BrandMark from '@/components/Icons/BrandMark'
import ExplorePrograms from '@/components/Layout/Navbar/ExplorePrograms'
import OnboardingNavbar from '@/components/Onboarding/OnboardingNavbar'
import ShwLink from '@/components/ShwLink'
import { almostLargeScreen } from '@/components/UI/common.styled'
import UserMenu from '@/components/UserMenu'

import NavItem from './NavItem'
import SimplifiedNavbar from './SimplifiedNavbar'
import { navHeight } from './styled'
import type { NavbarDropdown, NavbarItem, NavbarLink } from './types'

function BurgerIcon({ open }: { open: boolean }) {
  return open ? (
    <FontAwesomeIcon icon={faTimes} color={chakraTokenToVar('fg')} />
  ) : (
    <FontAwesomeIcon icon={faBars} color={chakraTokenToVar('fg')} />
  )
}

function DrawerNavButton({
  label,
  hideDrawer,
  ref,
  ...props
}: ButtonProps & {
  label: string | (() => React.ReactElement)
  hideDrawer?: () => void
  ref?: React.ForwardedRef<HTMLButtonElement | null>
}) {
  return (
    <Button
      ref={ref}
      variant="ghost"
      fontWeight="normal"
      color="fg"
      borderRadius={0}
      justifyContent="left"
      borderBottom="2px solid"
      borderColor="border"
      onClick={() => (hideDrawer ? hideDrawer() : null)}
      textDecoration="none"
      width="100%"
      padding="36px 0px 36px 16px"
      {...props}
    >
      {typeof label === 'string' ? (
        <Heading size="xl" as="h3">
          {label}
        </Heading>
      ) : (
        label()
      )}
    </Button>
  )
}

type NavLinksPropsBase = {
  items: NavbarItem[]
}

type NavLinksPropsDesktop = NavLinksPropsBase & {
  layout: 'desktop'
  openNavbarDropdownLabel: string | null
  setOpenNavbarDropdownLabel: (label: string | null) => void
}

type NavLinksPropsMobile = NavLinksPropsBase & {
  layout: 'mobile'
  hideDrawer: () => void
}

type NavLinksProps = NavLinksPropsDesktop | NavLinksPropsMobile

function NavLinks(props: NavLinksProps) {
  const { items, layout } = props
  const pathname = usePathname() ?? '/'
  const activeNavbarItem = getActiveNavbarLink(
    items.filter(item => item.type === 'link'),
    pathname
  )
  const itemRefs = React.useRef<(HTMLButtonElement | null)[]>([])

  const renderLinkItem = (item: NavbarLink) => {
    const { href, label } = item

    if (layout === 'mobile') {
      return (
        <ShwLink key={href} href={href}>
          <DrawerNavButton
            label={label}
            hideDrawer={props.hideDrawer}
            ref={element => itemRefs.current.push(element)}
          />
        </ShwLink>
      )
    }

    return (
      <NavItem
        href={href}
        key={href}
        active={activeNavbarItem === item}
        label={label}
        ref={element => itemRefs.current.push(element)}
        isClickable
      />
    )
  }

  const renderDropdownItem = (item: NavbarDropdown) => {
    const { label, Component } = item
    const isDesktop = layout === 'desktop'
    const isOpen = isDesktop && props.openNavbarDropdownLabel === label

    const handleClick = isDesktop
      ? () => {
          props.setOpenNavbarDropdownLabel?.(isOpen ? null : label)
        }
      : undefined

    return (
      <Box key={label}>
        <Box onClick={handleClick}>
          <NavItem
            label={label}
            active={isOpen}
            showDropdownIcon={isDesktop}
            ref={element => itemRefs.current.push(element)}
            isClickable={isDesktop}
          />
        </Box>
        {layout === 'mobile' ? <Component /> : null}
      </Box>
    )
  }

  return items.map(item => (item.type === 'link' ? renderLinkItem(item) : renderDropdownItem(item)))
}

const Navbar: React.FC<{
  hideLoginButton?: boolean
  forceShowOnboardingNavbar?: boolean
  showSimplifiedNavbar?: boolean
  customNavbarItems?: NavbarItem[]
}> = ({
  hideLoginButton = false,
  forceShowOnboardingNavbar = false,
  showSimplifiedNavbar = false,
  customNavbarItems,
}) => {
  const user = useUser()
  const pathname = usePathname() ?? ''
  const [isLgMinus] = useMediaQuery(`(max-width: ${almostLargeScreen})`)

  const [openNavbarDropdownLabel, setOpenNavbarDropdownLabel] = useState<string | null>(null)

  const items: NavbarItem[] = useMemo(
    () =>
      customNavbarItems ?? [
        {
          type: 'dropdown',
          label: 'Explore Programs',
          Component: ExplorePrograms,
        },
        { type: 'link', label: 'About', href: '/about' },
        { type: 'link', label: 'FAQ', href: faq.getUrl() },
        { type: 'link', label: 'Stories', href: '/stories' },
        { type: 'link', label: 'Partners', href: '/partnerships' },
        { type: 'link', label: 'Donate', href: getDonateUrl() },
      ],
    [customNavbarItems]
  )

  const DropdownContents = useMemo(() => {
    if (openNavbarDropdownLabel == null) {
      return null
    }
    for (const item of items) {
      if (item.type === 'dropdown' && item.label === openNavbarDropdownLabel) {
        return item.Component
      }
    }
    return null
  }, [openNavbarDropdownLabel, items])

  const { isOpen, onOpen, onClose } = useDisclosure()

  useEffect(() => {
    if (openNavbarDropdownLabel != null && !isLgMinus) {
      onOpen()
    } else {
      onClose()
    }
  }, [openNavbarDropdownLabel, onOpen, onClose, isLgMinus])

  const [isMenuOpen, setIsMenuOpen] = useState(false)

  // Special case if user is onboarding
  if (
    (!user.isLoggedOut && !user.emailVerified) ||
    isUserOnboarding(user) ||
    forceShowOnboardingNavbar
  ) {
    return <OnboardingNavbar />
  }

  if (showSimplifiedNavbar) {
    return <SimplifiedNavbar />
  }

  const handleSignupClick = () => {
    trackEvent({
      category: 'Button Click',
      action: 'Navbar Sign In',
    })
  }

  const hideDrawer = () => {
    setIsMenuOpen(false)
  }

  return (
    <>
      <Box
        height={navHeight}
        bg="bg"
        borderBottom="2px solid"
        borderColor="border.emphasized"
        position="relative"
      >
        <Flex
          alignItems="center"
          height="100%"
          py={0}
          px={{ base: '16px', lg: '24px' }}
          justifyContent={{ base: 'space-between', lg: 'normal' }}
          position="relative"
        >
          <Button
            position="absolute"
            display={{ base: 'flex', lg: 'none' }}
            variant="ghost"
            appearance="none"
            border={0}
            p={0}
            width="40px"
            height="40px"
            borderRadius="50%"
            bgColor="bg"
            _hover={{ bgColor: 'bg.subtle', outline: 'none' }}
            _active={{ outline: 'none' }}
            _focus={{ outline: 'none' }}
            onClick={() => setIsMenuOpen(prevIsMenuOpen => !prevIsMenuOpen)}
          >
            <BurgerIcon open={isMenuOpen} />
          </Button>
          <Flex alignItems="center" mr="16px" position="absolute" height="100%" role="group">
            <ShwLink href={user.isLoggedOut ? '/' : getDashboardUrl()}>
              <Center display={{ base: 'none', sm: 'none', md: 'none', lg: 'flex', xl: 'none' }}>
                <BrandMark width={170} height={20} textColor="fg" />
              </Center>
              <Center display={{ base: 'none', xl: 'flex' }}>
                <BrandMark height={32} textColor="fg" />
              </Center>
            </ShwLink>
          </Flex>
          {user.isLoggedOut ? (
            <Flex
              alignItems="center"
              position="absolute"
              left="70px"
              right="70px"
              justify="center"
              top="2px"
              height="100%"
              display={['flex', 'flex', 'flex', 'none']}
            >
              <ShwLink href="/">
                <Box>
                  <BrandMark height={20} textColor="fg" />
                </Box>
              </ShwLink>
            </Flex>
          ) : null}
          <Flex
            position="absolute"
            left="0"
            right="0"
            justifyContent="center"
            alignItems="center"
            height="100%"
            userSelect="none"
            m={0}
            p={0}
            color="fg"
            display={{ base: 'none', lg: 'flex' }}
          >
            <NavLinks
              items={items}
              layout="desktop"
              openNavbarDropdownLabel={openNavbarDropdownLabel}
              setOpenNavbarDropdownLabel={setOpenNavbarDropdownLabel}
            />
          </Flex>
          {hideLoginButton ? null : (
            <Flex
              position="absolute"
              right={{ base: '16px', lg: '24px' }}
              width="auto"
              height="100%"
              color="primary.contrast"
              alignItems="center"
            >
              <UserMenu />
            </Flex>
          )}
          <Flex position="absolute" top="100%" width="100%" pt="24px" justifyContent="center">
            <Fade in={isOpen}>
              {DropdownContents != null ? (
                <Box
                  width="500px"
                  borderRadius="16px"
                  bgColor="bg"
                  border="2px solid"
                  borderColor="border.emphasized"
                >
                  <DropdownContents />
                </Box>
              ) : null}
            </Fade>
          </Flex>
        </Flex>
      </Box>
      <Drawer
        isOpen={isMenuOpen && isLgMinus}
        onClose={() => setIsMenuOpen(false)}
        placement="left"
        autoFocus={false}
        size="full"
      >
        <DrawerOverlay mt="64px" />
        <DrawerContent mt="64px" height="calc(100vh - 64px)" overflowY="scroll" px="16px" py="24px">
          {user.isLoggedOut ? null : (
            <Box ml={2} mb={2}>
              <ShwLink href="/">
                <Box>
                  <BrandMark height={32} textColor="fg" />
                </Box>
              </ShwLink>
            </Box>
          )}
          <NavLinks items={items} layout="mobile" hideDrawer={hideDrawer} />
          <SignUpLink href={pathname ?? null}>
            <Box onClick={handleSignupClick}>
              <DrawerNavButton label="Sign Up" hideDrawer={hideDrawer} />
            </Box>
          </SignUpLink>
        </DrawerContent>
      </Drawer>
    </>
  )
}

export default Navbar
